import { useEffect, useState } from "react";
import { MapPinned, Plus, Save, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const TEXT_FIELDS = [
  ["address", "Address", "Street address of the school or site"],
  ["reporting_location", "Reporting location", "Where to report on day one (building, entrance, room, who to ask for)"],
  ["arrival_instructions", "Arrival instructions", "Arrival time, sign-in, badge pickup…"],
  ["access_instructions", "Access instructions", "Doors, key cards, buzzer, escort rules…"],
  ["military_base_access", "Military base access", "Base pass process, gate, ID needed, sponsor, lead time (leave blank if not a base)"],
  ["parking", "Parking", "Where to park, permits, cost"],
  ["dress_code", "Dress code", ""],
  ["id_visitor_rules", "ID and visitor rules", "ID to carry, visitor badge, sign-in book…"],
  ["emergency_procedures", "Emergency procedures", "Lockdown, fire, medical, who to call"],
  ["clock_in_instructions", "Connecteam clock-in", "Leave blank to use the TAG default wording"],
  ["absence_instructions", "Absences and lateness", "Leave blank to use the TAG default wording"],
];
const CONTACTS = [["check_in", "Check-in contact"], ["coordinator", "Coordinator"], ["front_office", "Front office"], ["emergency", "Emergency contact"]];

export const ClientSiteProfileEditor = ({ client, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/clients/${client.id}/site-profile`).then((response) => setForm(response.data.profile)).catch((e) => setError(formatApiError(e)));
  }, [client.id]);

  const setField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const setContact = (key, part, value) => setForm((current) => ({ ...current, contacts: { ...current.contacts, [key]: { ...current.contacts[key], [part]: value } } }));
  const setItem = (index, label) => setForm((current) => ({ ...current, manual_clearances: current.manual_clearances.map((item, i) => (i === index ? { ...item, label } : item)) }));

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api.put(`/clients/${client.id}/site-profile`, form);
      onSaved(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="inline-edit-panel site-profile-editor" data-testid="site-profile-editor">
      <div className="section-heading">
        <div>
          <h2><MapPinned size={17} /> {client.name} · site profile</h2>
          <p>What every provider needs before day one. Address, reporting location, access and parking changes send “Assignment updated” to providers already placed here.</p>
        </div>
        <button aria-label="Close" className="projection-icon-button" data-testid="site-profile-close" type="button" onClick={onClose}><X size={16} /></button>
      </div>
      {error && <p className="form-message error-message" data-testid="site-profile-error">{error}</p>}
      {form && (
        <form className="drawer-form-grid" data-testid="site-profile-form" onSubmit={save}>
          <div className="form-section-label drawer-form-full">Location and access</div>
          {TEXT_FIELDS.slice(0, 6).map(([key, label, hint]) => (
            <label className="drawer-form-full" key={key}><span className="field-label">{label}</span><textarea data-testid={`site-${key}`} placeholder={hint} rows={key === "address" ? 2 : 2} value={form[key]} onChange={(e) => setField(key, e.target.value)} /></label>
          ))}
          <div className="form-section-label drawer-form-full">Site contacts</div>
          {CONTACTS.map(([key, label]) => (
            <fieldset className="site-contact-row drawer-form-full" data-testid={`site-contact-${key}`} key={key}>
              <legend>{label}</legend>
              <input data-testid={`site-contact-${key}-name`} placeholder="Name" value={form.contacts[key].name} onChange={(e) => setContact(key, "name", e.target.value)} />
              <input data-testid={`site-contact-${key}-phone`} placeholder="Phone" value={form.contacts[key].phone} onChange={(e) => setContact(key, "phone", e.target.value)} />
              <input data-testid={`site-contact-${key}-email`} placeholder="Email" type="email" value={form.contacts[key].email} onChange={(e) => setContact(key, "email", e.target.value)} />
            </fieldset>
          ))}
          <div className="form-section-label drawer-form-full">Rules and procedures</div>
          {TEXT_FIELDS.slice(6).map(([key, label, hint]) => (
            <label className="drawer-form-full" key={key}><span className="field-label">{label}</span><textarea data-testid={`site-${key}`} placeholder={hint} rows={2} value={form[key]} onChange={(e) => setField(key, e.target.value)} /></label>
          ))}
          <div className="form-section-label drawer-form-full">Pre-start items (checked off per assignment, in addition to the client's compliance requirements)</div>
          <div className="drawer-form-full site-manual-items" data-testid="site-manual-items">
            {form.manual_clearances.map((item, index) => (
              <div className="site-manual-item" key={item.id || index}>
                <input data-testid={`site-manual-item-${index}`} placeholder="e.g. District badge issued, Base pass approved" value={item.label} onChange={(e) => setItem(index, e.target.value)} />
                <button aria-label="Remove item" className="projection-icon-button" data-testid={`site-manual-remove-${index}`} type="button" onClick={() => setField("manual_clearances", form.manual_clearances.filter((_, i) => i !== index))}><Trash2 size={14} /></button>
              </div>
            ))}
            <button className="button button-ghost" data-testid="site-manual-add" type="button" onClick={() => setField("manual_clearances", [...form.manual_clearances, { id: "", label: "" }])}><Plus size={14} /> Add pre-start item</button>
          </div>
          <div className="drawer-form-full form-actions">
            {form.updated_at && <small className="pairing-meta">Last saved {new Date(form.updated_at).toLocaleString()} by {form.updated_by_name}</small>}
            <button className="button button-primary" data-testid="site-profile-save" disabled={busy} type="submit"><Save size={15} /> {busy ? "Saving…" : "Save site profile"}</button>
          </div>
        </form>
      )}
    </section>
  );
};
